import type { Decimal } from '../math/num';
import { D, pow } from '../math/num';
import type { PortSpec } from '../graph/types';
import { getPartDef } from './parts/partDefs';

/**
 * The Mech Core — the singular, intrinsic power source at the center of every
 * Mech (THE_MIND_AND_PARTS §2). It is NOT loot: it's always equipped, can't be
 * scrapped or unequipped, and is upgraded with Components like any part. Power
 * comes from the Mech, so the Core is where it originates.
 *
 * Two things grow it:
 *   • Upgrading (Components) raises its base Power.
 *   • Ascending into a bigger Mech grows the number of OUTPUT taps it exposes,
 *     so you can feed more attacks at full strength (or stack taps onto one to
 *     supercharge it — their Power adds).
 *
 * Combined with the connection-splitting penalty in the evaluator (a tap's Power
 * divides evenly among everything it feeds), output count is a real progression
 * lever, not a cosmetic one.
 */

export const MECH_CORE_DEF_ID = 'src.mech_core';
/** Fixed instance id so the Core is addressable across saves/migrations. */
export const MECH_CORE_INSTANCE_ID = 'core';

export function isMechCore(defId: string): boolean {
  return defId === MECH_CORE_DEF_ID;
}
export function isMechCoreInstance(instanceId: string | null): boolean {
  return instanceId === MECH_CORE_INSTANCE_ID;
}

/** Output taps the Core exposes at a given Mech tier. Starts at 2 (the starter
 *  Punch + a spare), grows +1 per Ascension, capped so late builds stay legible. */
export function coreOutputCount(tier: number): number {
  return Math.min(12, Math.max(1, tier + 1));
}

/** Power multiplier the Core gains from being a bigger Mech (T1 = ×1, so the
 *  starter math is unchanged). Modest — most growth still comes from upgrades,
 *  more taps, frame level, and Cores. */
export function coreTierMult(tier: number): Decimal {
  return pow(D(1.5), Math.max(0, tier - 1));
}

/** Per-level Core power growth — the game's ONE deliberate exponential part
 *  curve (P37). Loot parts now grow with diminishing returns, so the intrinsic,
 *  infinitely-leveled Core is the long-run engine that keeps pace with the
 *  wave walls. Level 1 = ×1, so starter goldens are unchanged. */
export const CORE_LEVEL_GROWTH = 1.12;
export function coreLevelMult(level: number): Decimal {
  return pow(D(CORE_LEVEL_GROWTH), Math.max(0, level - 1));
}

/** The Core's output ports for a tier (o1..oN). */
export function corePorts(tier: number): PortSpec[] {
  return Array.from({ length: coreOutputCount(tier) }, (_, i) => ({
    id: `o${i + 1}`,
    direction: 'out' as const,
    flow: 'power' as const,
    role: 'flow' as const,
  }));
}

/** Effective ports of a node, accounting for the Core's tier-dynamic outputs.
 *  Everything else uses its static PartDef ports. */
export function portsForNode(defId: string, tier: number): PortSpec[] {
  if (isMechCore(defId)) {
    const inPorts = getPartDef(defId).ports.filter((p) => p.direction === 'in');
    return [...inPorts, ...corePorts(tier)];
  }
  return getPartDef(defId).ports;
}
